import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { Overlay } from '@trello/nachos/overlay';
import { RouteId, useRouteId } from '@trello/router';

import {
  closeGAAnnouncementModal,
  useIsGAAnnouncementModalOpen,
} from '../betaGASharedState';
import { usePersonalProductivityCohortValue } from '../usePersonalProductivityCohortValue';
import { GAAnnouncementModalWithOptIn } from './GAAnnouncementModalWithOptIn';
import { GAAnnouncementModalWithoutOptIn } from './GAAnnouncementModalWithoutOptIn';

import styles from './GAAnnouncementModal.less';

const noop = () => {};

export const GAAnnouncementModal: FunctionComponent = () => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { value: isGateEnabled } = useFeatureGate(
    'trello_beta_ga_announcement',
  );
  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const hasSeenAnnouncementModal = isOneTimeMessageDismissed(
    'beta-ga-announcement-modal',
  );
  const shouldShowAnnouncementModalAgain = useIsGAAnnouncementModalOpen();
  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const isOptedIn = personalProductivityCohort === 'opted_in';
  const routeId = useRouteId();
  const isBoardRoute = routeId === RouteId.BOARD;

  const shouldShowModal =
    ((isModalOpen && !hasSeenAnnouncementModal) ||
      shouldShowAnnouncementModalAgain) &&
    isGateEnabled &&
    isBoardRoute;

  const onClose = useCallback(() => {
    setIsModalOpen(false);
    closeGAAnnouncementModal();
    if (!hasSeenAnnouncementModal) {
      dismissOneTimeMessage('beta-ga-announcement-modal');
    }
  }, [dismissOneTimeMessage, hasSeenAnnouncementModal]);

  useEffect(() => {
    if (shouldShowModal) {
      Analytics.sendViewedComponentEvent({
        componentType: 'modal',
        componentName: 'gaAnnouncementModal',
        source: 'boardScreen',
        attributes: {
          cohort: personalProductivityCohort,
        },
      });
    }
  }, [personalProductivityCohort, shouldShowModal]);

  const ModalComponent = isOptedIn
    ? GAAnnouncementModalWithoutOptIn
    : GAAnnouncementModalWithOptIn;

  return (
    shouldShowModal && (
      <Overlay
        className={styles.overlay}
        closeOnOutsideClick={false}
        onClose={noop}
      >
        <ModalComponent onClose={onClose} />
      </Overlay>
    )
  );
};
